import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import toast, { Toaster } from 'react-hot-toast';
import { CiFilter, CiSearch } from "react-icons/ci";
import { MdFormatListBulleted, MdLayers } from "react-icons/md";
import '../styles/timesheet.css';
import Footer from './Footer';

import MainLayout from './MainLayout';
import { useAuth } from '../context/AuthContext';
import { getLeavesByEmployee, isWFH } from '../services/leaveApiService';
import { getStatus } from '../services/attendanceService';

export default function Timesheet({ isOnLeaveToday, isModalMode, onClose }) {
  const navigate = useNavigate();
  const [view, setView] = useState(isModalMode ? 'form' : 'list');
  const [timesheets, setTimesheets] = useState([]);
  const [filteredTimesheets, setFilteredTimesheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isGrouped, setIsGrouped] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  const [formData, setFormData] = useState({
    project: '',
    description: '',
    startTime: '',
    endTime: '',
    breakHours: '1.00',
    hours: ''
  });
  const [addedProjects, setAddedProjects] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [fetchedTimes, setFetchedTimes] = useState({
    checkIn: null,
    checkOut: null
  });

  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { user, token } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const toLocalISOString = (dtInput) => {
    if (!dtInput) return "";
    const dt = new Date(dtInput);
    if (isNaN(dt.getTime())) return "";
    const offset = dt.getTimezoneOffset() * 60000;
    return new Date(dt.getTime() - offset).toISOString().slice(0, 16);
  };

  // Fetch history on load
  useEffect(() => {
    fetchTimesheetHistory();
    fetchProjects();
    fetchHolidays();
    if (user?.empId && token) {
      fetchLeaves();
      fetchCurrentStatus();
    }
  }, [user?.empId, token]);

  const fetchCurrentStatus = async () => {
    try {
      const res = await getStatus(token);
      const record = res?.data?.record || res?.record || res?.data || res;
      if (record) {
        setFetchedTimes({
          checkIn: record.check_in || record.checkIn || record.checkin_time || null,
          checkOut: record.check_out || record.checkOut || record.checkout_time || null
        });
      }
    } catch (e) {
      console.error('Failed to fetch status for timesheet', e);
    }
  };

  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/holidays`);
      setHolidays(res.data || []);
    } catch (e) {
      console.error('Failed to load holidays', e);
    }
  };

  const fetchLeaves = async () => {
    try {
      const res = await getLeavesByEmployee(user.empId, token);
      setLeaves(res || []);
    } catch (e) {
      console.error('Failed to load leaves', e);
    }
  };

  // Apply filter when timesheets or search query changes
  useEffect(() => {
    applyFilter();
  }, [timesheets, searchQuery, filterType, customDateRange, projects]);

  useEffect(() => {
    const checkOpenForm = () => {
      const shouldOpenForm = localStorage.getItem("openTimesheetForm");

      if (shouldOpenForm === "true") {
        setView("form");

        // Auto-fetch times from localStorage if form opened explicitly via navigation
        const lastIn = localStorage.getItem("lastCheckIn") || fetchedTimes.checkIn;
        const lastOut = localStorage.getItem("lastCheckOut") || fetchedTimes.checkOut;

        const now = new Date();
        let newStartTime = formData.startTime;
        let newEndTime = formData.endTime;

        if (lastIn) {
          const d = new Date(lastIn);
          if (!isNaN(d)) {
            newStartTime = toLocalISOString(new Date(now.getFullYear(), now.getMonth(), now.getDate(), d.getHours(), d.getMinutes()));
          }
        }
        
        if (lastOut) {
          const d = new Date(lastOut);
          if (!isNaN(d)) {
            newEndTime = toLocalISOString(new Date(now.getFullYear(), now.getMonth(), now.getDate(), d.getHours(), d.getMinutes()));
          }
        }

        if (lastIn || lastOut) {
          setFormData(prev => ({
            ...prev,
            startTime: lastIn ? newStartTime : prev.startTime,
            endTime: lastOut ? newEndTime : prev.endTime
          }));
        }

        localStorage.removeItem("openTimesheetForm");
      }
    };

    checkOpenForm();
    // Also check periodically in case the tab was already open
    const interval = setInterval(checkOpenForm, 500);
    return () => clearInterval(interval);
  }, [fetchedTimes, formData.startTime, formData.endTime]);

  useEffect(() => {
    // If the form is open and we just received the check-in time from the API, update it
    if (view === 'form' && fetchedTimes.checkIn) {
      setFormData(prev => {
        // Only update if it's currently the default 09:30 or empty, so we don't overwrite user edits
        if (!prev.startTime || prev.startTime.includes('09:30')) {
          const now = new Date();
          const d = new Date(fetchedTimes.checkIn);
          let newTimeStr = toLocalISOString(fetchedTimes.checkIn);
          if (!isNaN(d)) {
            newTimeStr = toLocalISOString(new Date(now.getFullYear(), now.getMonth(), now.getDate(), d.getHours(), d.getMinutes()));
          }
          return { ...prev, startTime: newTimeStr };
        }
        return prev;
      });
    }
  }, [fetchedTimes.checkIn, view]);

  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(formData.startTime);
      const end = new Date(formData.endTime);
      let diffHrs = (end - start) / (1000 * 60 * 60);
      if (diffHrs >= 0) {
        const breakVal = parseFloat(formData.breakHours) || 0;
        let adjustedHrs = diffHrs > breakVal ? diffHrs - breakVal : diffHrs;
        setFormData(prev => ({ ...prev, hours: adjustedHrs.toFixed(2) }));
      }
    }
  }, [formData.startTime, formData.endTime, formData.breakHours]);

  const groupedTimesheets = useMemo(() => {
    if (!filteredTimesheets) return {};

    return filteredTimesheets.reduce((acc, ts) => {
      const project = projects.find(p => String(p.id) === String(ts.project) || p.sfprojectsid === ts.project);
      const key = project?.name || 'Unknown Project';

      if (!acc[key]) acc[key] = [];
      acc[key].push(ts);
      return acc;
    }, {});
  }, [filteredTimesheets, projects]);



  const fetchTimesheetHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/timesheet/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimesheets(response.data.data || []);
    } catch (error) {
      console.error('Failed to load history');
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/timesheet/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Directly take the data array from the timesheet projects endpoint
      const rawProjects = response.data?.data || [];
      const mapped = (Array.isArray(rawProjects) ? rawProjects : [])
        .filter(p => p.status !== "Closed") // hide closed projects from timesheet
        .map(p => ({
          id: p.id,
          sfprojectsid: p.sfprojectsid || `PRJ-${p.id}`,
          name: p.project_name,
          status: p.status,
        }));
      setProjects(mapped);
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  const applyFilter = () => {
    const now = new Date();
    let filtered = [...timesheets];

    // 1. Date Filter (Old logic)
    switch (filterType) {
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        filtered = filtered.filter(ts => {
          const checkInDate = new Date(ts.check_in || ts.starttime);
          return checkInDate >= yesterday && checkInDate <= yesterdayEnd;
        });
        break;
      case 'thisWeek':
        const startOfWeek = new Date(now);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);
        filtered = filtered.filter(ts => {
          const checkInDate = new Date(ts.check_in || ts.starttime);
          return checkInDate >= startOfWeek;
        });
        break;
      case 'thisMonth':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        startOfMonth.setHours(0, 0, 0, 0);
        filtered = filtered.filter(ts => {
          const checkInDate = new Date(ts.check_in || ts.starttime);
          return checkInDate >= startOfMonth;
        });
        break;
      case 'custom':
        if (customDateRange.startDate && customDateRange.endDate) {
          const startDate = new Date(customDateRange.startDate);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(customDateRange.endDate);
          endDate.setHours(23, 59, 59, 999);
          filtered = filtered.filter(ts => {
            const checkInDate = new Date(ts.check_in || ts.starttime);
            return checkInDate >= startDate && checkInDate <= endDate;
          });
        }
        break;
      case 'all':
      default:
        break;
    }

    // 2. Search Text Filter
    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(ts => {
        const project = projects.find(p => String(p.id) === String(ts.project) || p.sfprojectsid === ts.project);
        const projectName = project ? project.name.toLowerCase() : '';

        const startTimeStr = ts.starttime ? formatTime(ts.starttime).toLowerCase() : '';
        const endTimeStr = ts.endtime ? formatTime(ts.endtime).toLowerCase() : '';
        const statusText = 'submitted';

        return (
          projectName.includes(lowerQuery) ||
          startTimeStr.includes(lowerQuery) ||
          endTimeStr.includes(lowerQuery) ||
          statusText.includes(lowerQuery)
        );
      });
    }

    setFilteredTimesheets(filtered);
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    setShowFilterDropdown(false);
    if (type !== 'custom') {
      setCustomDateRange({ startDate: '', endDate: '' });
    }
  };

  const handleCustomDateApply = () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      applyFilter();
      setShowFilterDropdown(false);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const handleTimesheetClick = (timesheet) => {
    if (timesheet.hasbeensubmitted === 'No') {
      setView('form');

      const clickedDateStr = getLocalDateStr(new Date(timesheet.starttime || timesheet.check_in));

      const sameDateSavedTimesheets = timesheets.filter(ts => {
        if (ts.hasbeensubmitted !== 'No') return false;
        const tsDateStr = getLocalDateStr(new Date(ts.starttime || ts.check_in));
        return tsDateStr === clickedDateStr;
      });

      const projectsToAdd = sameDateSavedTimesheets.map(ts => ({
        id: ts.sftimesheetsid,
        project: ts.project,
        description: ts.comments || '',
        startTime: ts.starttime ? toLocalISOString(ts.starttime) : '',
        endTime: ts.endtime ? toLocalISOString(ts.endtime) : '',
        hours: ts.hours
      }));

      setAddedProjects(projectsToAdd);

      setFormData({
        project: '',
        description: '',
        startTime: '',
        endTime: '',
        hours: ''
      });
      setEditIndex(null);
    } else {
      setSelectedTimesheet(timesheet);
      setView('detail');
    }
  };

  const handleNewClick = () => {
    setView('form');

    // Fetch actual check-in and check-out times from local state or localStorage
    const lastIn = fetchedTimes.checkIn || localStorage.getItem("lastCheckIn");
    const lastOut = fetchedTimes.checkOut || localStorage.getItem("lastCheckOut");

    const now = new Date();
    // Use check-in time as start time, default to 9:30 if not available
    let st = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 9, 30, 0, 0);
    let et = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 18, 30, 0, 0);

    if (lastIn) {
      const d = new Date(lastIn);
      if (!isNaN(d)) {
        st = new Date(now.getFullYear(), now.getMonth(), now.getDate(), d.getHours(), d.getMinutes());
      }
    }

    if (lastOut) {
      const d = new Date(lastOut);
      if (!isNaN(d)) {
        et = new Date(now.getFullYear(), now.getMonth(), now.getDate(), d.getHours(), d.getMinutes());
      }
    }

    const breakVal = 1;
    let initialHrs = (et - st) / (1000 * 60 * 60);
    initialHrs = initialHrs > breakVal ? initialHrs - breakVal : initialHrs;

    setFormData({
      id: null,
      project: '',
      description: '',
      startTime: toLocalISOString(st),
      endTime: toLocalISOString(et),
      breakHours: '1.00',
      hours: initialHrs > 0 ? initialHrs.toFixed(2) : '0.00'
    });
    setAddedProjects([]);
    setEditIndex(null);
  };

  const handleAddToTimesheetList = async () => {
    if (!formData.project) {
      toast.error('Please select a project');
      return;
    }

    if (!formData.startTime || !formData.endTime || !formData.hours) {
      toast.error('Please enter start time, end time, and hours');
      return;
    }

    /* Commented out as per request
    if (fetchedTimes.checkIn && new Date(formData.startTime) < new Date(fetchedTimes.checkIn)) {
      toast.error('Start time cannot be earlier than actual check-in time.');
      return;
    }

    if (fetchedTimes.checkOut && new Date(formData.endTime) > new Date(fetchedTimes.checkOut)) {
      toast.error('End time cannot be later than actual check-out time.');
      return;
    }
    */

    const plainText = formData.description.replace(/<(.|\n)*?>/g, '').trim();
    if (!plainText) {
      toast.error('Please enter description');
      return;
    }

    // Create entry object
    const newEntry = {
      id: formData.id || null,
      project: formData.project,
      description: formData.description,
      startTime: formData.startTime,
      fullStartTime: formData.startTime ? new Date(formData.startTime).toISOString() : '',
      endTime: formData.endTime,
      fullEndTime: formData.endTime ? new Date(formData.endTime).toISOString() : '',
      hours: formData.hours,
      status: 'Saved'
    };

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payloadEntries = [newEntry];

      const response = await axios.post(`${API_BASE_URL}/api/timesheet/submit`, { entries: payloadEntries }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const savedData = response.data.data[0];
      const newId = savedData?.sftimesheetsid || savedData?.id || formData.id;
      newEntry.id = newId;

      if (editIndex !== null) {
        const updated = [...addedProjects];
        updated[editIndex] = newEntry;
        setAddedProjects(updated);
        setEditIndex(null);
        toast.success('Project details updated in list and saved as Draft');
      } else {
        const isDuplicate = addedProjects.some(p => p.project === formData.project);
        if (isDuplicate) {
          toast.error('This project is already in your list. Please edit the existing entry or choose another project.');
          setLoading(false);
          return;
        }

        setAddedProjects([...addedProjects, newEntry]);
        toast.success('Project added to list and saved as Draft');
      }

      fetchTimesheetHistory();

      const now = new Date();
      const st = new Date(now).setHours(9, 30, 0, 0);
      const et = new Date(now).setHours(18, 30, 0, 0);

      setFormData({
        id: null,
        project: '',
        description: '',
        startTime: toLocalISOString(st),
        endTime: toLocalISOString(et),
        breakHours: '1.00',
        hours: '8.00'
      });

      setTimeout(() => {
        const el = document.getElementById('project-select');
        if (el) el.focus();
      }, 100);

    } catch (error) {
      console.error(error);
      toast.error('Failed to save to list');
    } finally {
      setLoading(false);
    }
  };

  const handleEditListEntry = (index) => {
    const entry = addedProjects[index];
    setFormData({
      id: entry.id || null,
      project: entry.project,
      description: entry.description,
      startTime: entry.startTime,
      endTime: entry.endTime,
      hours: entry.hours
    });
    setEditIndex(index);
  };

  const handleDeleteListEntry = async (index) => {
    const entry = addedProjects[index];
    const updated = addedProjects.filter((_, i) => i !== index);
    setAddedProjects(updated);
    if (editIndex === index) {
      setEditIndex(null);
      setFormData({ project: '', description: '', startTime: '', endTime: '', breakHours: '1.00', hours: '' });
    } else if (editIndex > index) {
      setEditIndex(editIndex - 1);
    }

    if (entry.id) {
      try {
        const token = localStorage.getItem('token');
        try {
          await axios.delete(`${API_BASE_URL}/api/timesheet/${entry.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (deleteError) {
          await axios.put(`${API_BASE_URL}/api/timesheet/${entry.id}/status`, { status: 'Deleted' }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        fetchTimesheetHistory();
      } catch (error) {
        console.error("Failed to delete draft from DB", error);
      }
    }

    toast.success('Project removed from list');
  };

  const getPreviousWorkingDay = (currentDate) => {
    let d = new Date(currentDate);
    d.setDate(d.getDate() - 1);
    while (true) {
      const day = d.getDay();
      const isWeekend = day === 0 || day === 6;

      const offset = d.getTimezoneOffset() * 60000;
      const localDateStr = new Date(d.getTime() - offset).toISOString().split('T')[0];

      const isHoliday = holidays.some(h => {
        return h.holiday_date && String(h.holiday_date).startsWith(localDateStr);
      });
      const isOnLeave = leaves.some(l => {
        if (isWFH(l)) return false; // WFH is a normal working day, not a leave!
        const status = (l.status || "").toUpperCase();
        if (status !== "APPROVED" && status !== "PENDING") return false;

        const reqType = (l.request_type || "").toUpperCase();
        const unitType = (l.unit_type || "").toUpperCase();
        const leaveName = (l.leave_type_name || l.name || "").toUpperCase();
        const units = parseFloat(l.total_units ?? l.totalUnits ?? l.duration ?? 1);

        // Permission or Half-Day means employee works remaining hours, so it's a working day
        if (reqType === "PERMISSION" || unitType === "HOUR" || unitType === "HOURS" || leaveName.includes("PERMISSION")) {
          return false;
        }
        if (reqType === "HALF_DAY" || (unitType === "DAY" && units === 0.5)) {
          return false;
        }

        if (units < 1) return false;

        const lStartDate = getLocalDateStr(new Date(l.start_date || l.startDate));
        const lEndDate = getLocalDateStr(new Date(l.end_date || l.endDate || l.start_date || l.startDate));
        
        return localDateStr >= lStartDate && localDateStr <= lEndDate;
      });

      if (isWeekend || isHoliday || isOnLeave) {
        d.setDate(d.getDate() - 1);
      } else {
        return d;
      }
    }
  };

  const getLocalDateStr = (dObj) => {
    if (!dObj || isNaN(dObj.getTime())) return "";
    const offset = dObj.getTimezoneOffset() * 60000;
    return new Date(dObj.getTime() - offset).toISOString().split('T')[0];
  };

  const effectiveIsOnLeaveToday = useMemo(() => {
    const todayStr = getLocalDateStr(new Date());
    return leaves.some(l => {
      if (isWFH(l)) return false;
      const status = (l.status || "").toUpperCase();
      if (status !== "APPROVED") return false;

      const reqType = (l.request_type || "").toUpperCase();
      const unitType = (l.unit_type || "").toUpperCase();
      const leaveName = (l.leave_type_name || l.name || "").toUpperCase();
      const units = parseFloat(l.total_units ?? l.totalUnits ?? l.duration ?? 1);

      // Do NOT block New timesheet button for Permission or Half-Day leaves
      if (reqType === "PERMISSION" || unitType === "HOUR" || unitType === "HOURS" || leaveName.includes("PERMISSION")) {
        return false;
      }
      if (reqType === "HALF_DAY" || (unitType === "DAY" && units === 0.5)) {
        return false;
      }

      if (units < 1) return false;

      const lStartDate = getLocalDateStr(new Date(l.start_date || l.startDate));
      const lEndDate = getLocalDateStr(new Date(l.end_date || l.endDate || l.start_date || l.startDate));
      return todayStr >= lStartDate && todayStr <= lEndDate;
    });
  }, [leaves]);

  const check8HourRule = (targetDateString, listToValidate) => {
    // calculate total hours for target date
    let totalHours = 0;

    // add existing hours from DB
    timesheets.forEach(ts => {
      const tsDateStr = getLocalDateStr(new Date(ts.starttime));
      if (tsDateStr === targetDateString) {
        totalHours += parseFloat(ts.hours || 0);
      }
    });

    // add current list hours
    listToValidate.forEach(entry => {
      const entryDateStr = getLocalDateStr(new Date(entry.startTime));
      if (entryDateStr === targetDateString) {
        totalHours += parseFloat(entry.hours || 0);
      }
    });

    // find permissions and half-day leaves for the target date
    let permissionHours = 0;
    let halfDayLeaveHours = 0;
    leaves.forEach(l => {
      if (isWFH(l)) return; // WFH days require standard working hours (8h), not treated as leave deduction
      const status = (l.status || "").toUpperCase();
      if (status === "APPROVED" || status === "PENDING") {
        const lStartDate = getLocalDateStr(new Date(l.start_date || l.startDate));
        const lEndDate = getLocalDateStr(new Date(l.end_date || l.endDate || l.start_date || l.startDate));

        if (targetDateString >= lStartDate && targetDateString <= lEndDate) {
          const reqType = (l.request_type || "").toUpperCase();
          const unitType = (l.unit_type || "").toUpperCase();
          const leaveName = (l.leave_type_name || l.name || "").toUpperCase();
          const units = parseFloat(l.total_units || l.totalUnits || l.duration || 0);

          if (reqType === "PERMISSION" || unitType === "HOURS" || unitType === "HOUR" || leaveName.includes("PERMISSION")) {
            permissionHours += units;
          } else if (reqType === "HALF_DAY" || (unitType === "DAY" && units === 0.5)) {
            halfDayLeaveHours += 4;
          }
        }
      }
    });

    const requiredHours = Math.max(0, 8 - permissionHours - halfDayLeaveHours);
    return {
      isValid: totalHours >= requiredHours,
      requiredHours
    };
  };

  const validateSubmission = (listToValidate) => {
    if (listToValidate.length === 0) {
      toast.error('Please add at least one entry to the timesheet list.');
      return false;
    }

    const uniqueDates = [...new Set(listToValidate.map(entry => getLocalDateStr(new Date(entry.startTime))))];

    // Check if any date in the submission is a full day leave
    for (const dStr of uniqueDates) {
      const displayDate = dStr.split('-').reverse().join('-');
      const isOnFullDayLeave = leaves.some(l => {
        if (isWFH(l)) return false; // WFH is not a leave - employees submit timesheets normally
        const status = (l.status || "").toUpperCase();
        if (status !== "APPROVED" && status !== "PENDING") return false;

        const reqType = (l.request_type || "").toUpperCase();
        const unitType = (l.unit_type || "").toUpperCase();
        const leaveName = (l.leave_type_name || l.name || "").toUpperCase();
        const units = parseFloat(l.total_units ?? l.totalUnits ?? l.duration ?? 1);

        // Never consider Permission or Half-Day as a full day leave
        if (reqType === "PERMISSION" || unitType === "HOUR" || unitType === "HOURS" || leaveName.includes("PERMISSION")) {
          return false;
        }
        if (reqType === "HALF_DAY" || (unitType === "DAY" && units === 0.5)) {
          return false;
        }

        if (units < 1) return false;

        const lStartDate = getLocalDateStr(new Date(l.start_date || l.startDate));
        const lEndDate = getLocalDateStr(new Date(l.end_date || l.endDate || l.start_date || l.startDate));
        
        return dStr >= lStartDate && dStr <= lEndDate;
      });

      if (isOnFullDayLeave) {
        toast.error(`You cannot submit a timesheet for ${displayDate} as you have applied for leave on this date.`);
        return false;
      }

      // Ensure only one submission per day
      const alreadySubmittedForDate = timesheets.some(ts => {
        const tsDateStr = getLocalDateStr(new Date(ts.starttime));
        return tsDateStr === dStr && ts.hasbeensubmitted === 'Yes';
      });

      if (alreadySubmittedForDate) {
        toast.error(`Timesheet for ${displayDate} has already been submitted. You can only submit once per day. Please add all projects to the list before submitting.`);
        return false;
      }
    }

    const todayStr = getLocalDateStr(new Date());
    const prevWorkingDay = getPreviousWorkingDay(new Date());
    const prevWorkingDayStr = getLocalDateStr(prevWorkingDay);

    // Check if previous working day was submitted
    let prevDaySubmitted = false;
    timesheets.forEach(ts => {
      const tsDateStr = getLocalDateStr(new Date(ts.starttime));
      if (tsDateStr === prevWorkingDayStr && ts.hasbeensubmitted === 'Yes') {
        prevDaySubmitted = true;
      }
    });

    // We only care to block if they are submitting today's timesheet but haven't finished yesterday's
    const isSubmittingToday = listToValidate.some(entry => getLocalDateStr(new Date(entry.startTime)) === todayStr);

    if (isSubmittingToday && !prevDaySubmitted) {
      const displayPrevDate = prevWorkingDayStr.split('-').reverse().join('-');
      toast.error(`Timesheet for previous working day (${displayPrevDate}) was not submitted. Please submit it first.`);
      return false;
    }

    // Check 8 hour rule for all dates being submitted
    for (const dStr of uniqueDates) {
      const displayDate = dStr.split('-').reverse().join('-');
      const rule = check8HourRule(dStr, listToValidate);
      if (!rule.isValid) {
        if (rule.requiredHours < 8) {
          toast.error(`8 hours timesheet is required for ${displayDate}. After permission/half-day deductions, remaining balance hours is ${rule.requiredHours}.`);
        } else {
          toast.error(`8 hours timesheet is required for ${displayDate}`);
        }
        return false;
      }
    }

    return true;
  };

  const handleActionClick = (e, status) => {
    e.preventDefault();

    let currentList = [...addedProjects];

    // If the input fields currently contain a valid project description detail, auto-add it before submitting
    if (formData.project && formData.startTime && formData.endTime && formData.hours) {
      /* Commented out as requested:
      if (fetchedTimes.checkIn) {
        const checkInTime = new Date(fetchedTimes.checkIn);
        checkInTime.setMinutes(checkInTime.getMinutes() - 5);
        if (new Date(formData.startTime) < checkInTime) {
          toast.error('Start time cannot be earlier than actual check-in time.');
          return;
        }
      }
      
      if (fetchedTimes.checkOut) {
        const checkOutTime = new Date(fetchedTimes.checkOut);
        checkOutTime.setMinutes(checkOutTime.getMinutes() + 5);
        if (new Date(formData.endTime) > checkOutTime) {
          toast.error('End time cannot be later than actual check-out time.');
          return;
        }
      }
      */

      const plainText = formData.description.replace(/<(.|\n)*?>/g, '').trim();
      if (plainText) {
        if (editIndex !== null) {
          currentList[editIndex] = {
            id: formData.id || currentList[editIndex].id,
            project: formData.project,
            description: formData.description,
            startTime: formData.startTime,
            endTime: formData.endTime,
            hours: formData.hours
          };
        } else {
          const isDuplicate = currentList.some(p => p.project === formData.project);
          if (!isDuplicate) {
            currentList.push({
              id: formData.id || null,
              project: formData.project,
              description: formData.description,
              startTime: formData.startTime,
              endTime: formData.endTime,
              hours: formData.hours
            });
          }
        }
      }
    }

    if (status === 'Submitted') {
      if (validateSubmission(currentList)) {
        setAddedProjects(currentList);
        processSubmit(status, currentList);
      }
    } else {
      if (currentList.length === 0) {
        toast.error('Please add at least one entry to the timesheet list before saving.');
        return;
      }
      setAddedProjects(currentList);
      processSubmit(status, currentList);
    }
  };

  const processSubmit = async (status, listToSubmit) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payloadEntries = listToSubmit.map(entry => ({
        ...entry,
        status: status,
        fullStartTime: entry.startTime ? new Date(entry.startTime).toISOString() : '',
        fullEndTime: entry.endTime ? new Date(entry.endTime).toISOString() : ''
      }));

      await axios.post(`${API_BASE_URL}/api/timesheet/submit`, { entries: payloadEntries }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(status === 'Saved' ? 'Timesheets saved as Draft!' : 'Timesheets submitted successfully!');

      if (status === 'Submitted' || status === 'Saved') {
        setFormData({ project: '', description: '', startTime: '', endTime: '', breakHours: '1.00', hours: '' });
        setAddedProjects([]);
        setEditIndex(null);
        setView('list');
        navigate('/team');
      }

      fetchTimesheetHistory();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Submit failed!');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelForm = () => {
    // If they have unsaved projects or form is partially filled out
    if (addedProjects.length > 0 || formData.project) {
      setShowCancelModal(true);
    } else {
      closeForm();
    }
  };

  const handleDiscardDrafts = async () => {
    try {
      const token = localStorage.getItem('token');
      let deletedAny = false;
      for (const p of addedProjects) {
        if (p.id) {
          try {
            await axios.delete(`${API_BASE_URL}/api/timesheet/${p.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            deletedAny = true;
          } catch (deleteError) {
            // Fallback to update status
            try {
              await axios.put(`${API_BASE_URL}/api/timesheet/${p.id}/status`, { status: 'Deleted' }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              deletedAny = true;
            } catch (statusError) {
              console.error("Could not delete draft", statusError);
            }
          }
        }
      }
      if (deletedAny) {
        toast.success("Draft discarded successfully");
        fetchTimesheetHistory();
      }
    } catch (e) {
      console.error(e);
    }
    closeForm();
  };

  const closeForm = () => {
    setShowCancelModal(false);
    setFormData({ project: '', description: '', startTime: '', endTime: '', breakHours: '1.00', hours: '' });
    setAddedProjects([]);
    setEditIndex(null);
    setView(isModalMode ? 'form' : 'list');
    setSelectedTimesheet(null);
    if (onClose) onClose();
  };

  const handleStatusUpdate = async (e, tsId, status) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/timesheet/${tsId}/status`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Timesheet submitted successfully!');
      fetchTimesheetHistory();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  const formatTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    if (isNaN(d)) return 'N/A';
    const local = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const day = String(local.getDate()).padStart(2, '0');
    const month = String(local.getMonth() + 1).padStart(2, '0');
    const year = local.getFullYear();
    const timeStr = d.toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).toUpperCase();
    return `${day}-${month}-${year}, ${timeStr}`;
  };

  const getFilterLabel = () => {
    switch (filterType) {
      case 'yesterday': return 'Yesterday';
      case 'thisWeek': return 'This Week';
      case 'thisMonth': return 'This Month';
      case 'custom': return 'Custom Range';
      default: return 'All Time';
    }
  };

  const rowProps = useMemo(() => ({
    items: filteredTimesheets || [],
    projects: projects || [],
    selectedTimesheet,
    onClick: handleTimesheetClick,
    formatTime
  }), [filteredTimesheets, projects, selectedTimesheet]);


  const isEmbedded = window.location.pathname.includes('/team');

  const pageContent = (
    <div className={isModalMode ? "" : "timesheet-page"}>
      <Toaster position="top-right" />

      {/* Header handled by MainLayout */}

      {!isModalMode && (
        <div className="timesheet-container">
          {/* MAIN PANEL - Table view */}
          <div className="timesheet-list-panel full-width" style={{ border: 'none', background: 'transparent' }}>
            <div className="list-header" style={{ padding: 0, borderBottom: 'none', marginBottom: '20px' }}>
              <div className="list-header-top" style={{ display: 'flex', gap: '15px', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div className="filter-container">
                    <div className="search-wrap" style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '6px 12px', minWidth: '220px' }}>
                      <CiSearch size={18} color="#9ca3af" />
                      <input
                        type="text"
                        placeholder="Search project, date, status..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ border: 'none', outline: 'none', background: 'transparent', marginLeft: '8px', fontSize: '13px', width: '100%' }}
                      />
                    </div>
                  </div>

                  <div className="filter-container">
                    <button
                      className="btn-filter"
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <CiFilter size={20} />
                      <span>Filter: {getFilterLabel()}</span>
                    </button>

                    {showFilterDropdown && (
                      <div className="filter-dropdown">
                        <button
                          className={filterType === 'all' ? 'active' : ''}
                          onClick={() => handleFilterChange('all')}
                        >
                          All Time
                        </button>
                        <button
                          className={filterType === 'yesterday' ? 'active' : ''}
                          onClick={() => handleFilterChange('yesterday')}
                        >
                          Yesterday
                        </button>
                        <button
                          className={filterType === 'thisWeek' ? 'active' : ''}
                          onClick={() => handleFilterChange('thisWeek')}
                        >
                          This Week
                        </button>
                        <button
                          className={filterType === 'thisMonth' ? 'active' : ''}
                          onClick={() => handleFilterChange('thisMonth')}
                        >
                          This Month
                        </button>
                        <button
                          className={filterType === 'custom' ? 'active' : ''}
                          onClick={() => setFilterType('custom')}
                        >
                          Custom Range
                        </button>

                        {filterType === 'custom' && (
                          <div className="custom-date-panel">
                            <input
                              type="date"
                              value={customDateRange.startDate}
                              onChange={(e) => setCustomDateRange({
                                ...customDateRange,
                                startDate: e.target.value
                              })}
                              max={new Date().toISOString().split('T')[0]}
                            />
                            <span>to</span>
                            <input
                              type="date"
                              value={customDateRange.endDate}
                              onChange={(e) => setCustomDateRange({
                                ...customDateRange,
                                endDate: e.target.value
                              })}
                              max={new Date().toISOString().split('T')[0]}
                            />
                            <button
                              className="btn-apply"
                              onClick={handleCustomDateApply}
                            >
                              Apply
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    className={`btn-filter ${isGrouped ? 'active-group' : ''}`}
                    onClick={() => setIsGrouped(!isGrouped)}
                  >
                    <MdLayers size={20} />
                    <span>Group by Project</span>
                  </button>

                </div>

                <div style={{ flex: 1 }}></div>

                {/* NEW BUTTON (Hidden in Embedded mode) */}
                {!isEmbedded && (
                  <button
                    className="btn-new"
                    style={{ marginLeft: '10px' }}
                    onClick={() => {
                      if (effectiveIsOnLeaveToday) {
                        toast.error("You cannot submit a timesheet while on approved leave.");
                        return;
                      }
                      handleNewClick();
                    }}
                    disabled={effectiveIsOnLeaveToday}
                  >
                    New
                  </button>
                )}
              </div>
            </div>

            <div className="timesheet-list list-full" style={{ padding: '0', background: 'white', borderRadius: '12px', overflow: 'hidden' }}>
              {!filteredTimesheets || filteredTimesheets.length === 0 ? (
                <div className="empty-state">
                  <p> No timesheets found</p>
                  <button className="btn-primary" onClick={handleNewClick}>
                    Submit your first timesheet
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="added-projects-table" style={{ width: '100%', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                    <thead style={{ backgroundColor: '#f9fafb' }}>
                      <tr>
                        {!isGrouped && <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Project</th>}
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Start Date & Time</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>End Date & Time</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Description</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Hours</th>
                        <th style={{ padding: '16px', textAlign: 'left', fontWeight: 'bold' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isGrouped ? (
                        Object.entries(groupedTimesheets).map(([groupName, items]) => (
                          <React.Fragment key={groupName}>
                            <tr style={{ backgroundColor: '#f3f4f6' }}>
                              <td colSpan="5" style={{ padding: '12px 16px', fontWeight: 'bold', borderBottom: '1px solid #e5e7eb' }}>
                                {groupName} <span style={{ marginLeft: '10px', backgroundColor: '#e5e7eb', borderRadius: '12px', padding: '2px 8px', fontSize: '12px' }}>{items.length}</span>
                              </td>
                            </tr>
                            {items.map(ts => (
                              <tr key={ts.sftimesheetsid} onClick={() => handleTimesheetClick(ts)} style={{ cursor: 'pointer', borderBottom: '1px solid #e5e7eb' }} className="hover-row">
                                <td style={{ padding: '12px 16px' }}>{ts.starttime ? formatTime(ts.starttime) : 'N/A'}</td>
                                <td style={{ padding: '12px 16px' }}>{ts.endtime ? formatTime(ts.endtime) : 'N/A'}</td>
                                <td style={{ padding: '12px 16px' }}>{ts.comments?.replace(/<[^>]*>/g, '').substring(0, 100)}...</td>
                                <td style={{ padding: '12px 16px', fontWeight: '600' }}>{ts.hours ? Number(ts.hours).toFixed(2) : 'N/A'}</td>
                                <td style={{ padding: '12px 16px' }}>
                                  {ts.hasbeensubmitted === 'No' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span className="status-badge" style={{ padding: '4px 12px', borderRadius: '12px', backgroundColor: '#e0e7ff', color: '#4f46e5', fontSize: '12px', fontWeight: 'bold' }}>Saved</span>
                                    </div>
                                  ) : (
                                    <span className="status-badge" style={{ padding: '4px 8px', borderRadius: '12px', backgroundColor: '#d1fae5', color: '#065f46', fontSize: '12px', fontWeight: 'bold' }}>✓ Submitted</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))
                      ) : (
                        filteredTimesheets.map(ts => {
                          const project = projects.find(p => String(p.id) === String(ts.project) || p.sfprojectsid === ts.project);
                          return (
                            <tr key={ts.sftimesheetsid} onClick={() => handleTimesheetClick(ts)} style={{ cursor: 'pointer', borderBottom: '1px solid #e5e7eb' }} className="hover-row">
                              <td style={{ padding: '12px 16px', fontWeight: '500' }}>{project?.name || 'Unknown Project'}</td>
                              <td style={{ padding: '12px 16px' }}>{ts.starttime ? formatTime(ts.starttime) : 'N/A'}</td>
                              <td style={{ padding: '12px 16px' }}>{ts.endtime ? formatTime(ts.endtime) : 'N/A'}</td>
                              <td style={{ padding: '12px 16px' }}>{ts.comments?.replace(/<[^>]*>/g, '').substring(0, 100)}...</td>
                              <td style={{ padding: '12px 16px', fontWeight: '600' }}>{ts.hours ? Number(ts.hours).toFixed(2) : 'N/A'}</td>
                              <td style={{ padding: '12px 16px' }}>
                                {ts.hasbeensubmitted === 'No' ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="status-badge" style={{ padding: '4px 12px', borderRadius: '12px', backgroundColor: '#e0e7ff', color: '#4f46e5', fontSize: '12px', fontWeight: 'bold' }}>Saved</span>
                                  </div>
                                ) : (
                                  <span className="status-badge" style={{ padding: '4px 8px', borderRadius: '12px', backgroundColor: '#d1fae5', color: '#065f46', fontSize: '12px', fontWeight: 'bold' }}>✓ Submitted</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DRAWER FOR NEW TIMESHEET FORM AND DETAILS */}
      {(view === 'form' || view === 'detail') && (
        <div className="timesheet-drawer-overlay" onClick={() => view === 'form' ? handleCancelForm() : closeForm()}>
          <div className="timesheet-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="form-header" style={{ padding: '8px 24px', borderBottom: '1px solid #e5e7eb', flexShrink: 0, background: 'white', zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                {view === 'form' ? 'Submit Timesheet' : 'Timesheet Details'}
              </h2>
              <button className="btn-close" onClick={() => view === 'form' ? handleCancelForm() : closeForm()} style={{ background: 'transparent', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', padding: 0, width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px' }}>✕</button>
            </div>

            {view === 'form' ? (
              <>
                <form onSubmit={(e) => e.preventDefault()} className="timesheet-form" style={{ padding: '8px 24px', margin: 0, flex: '1 1 auto', overflowY: 'auto', minHeight: 0, maxWidth: '100%' }}>

                  <div className="form-grid" style={{ gap: '12px' }}>
                    <div className="form-group">
                      <label style={{ fontSize: '12px', marginBottom: '4px' }}>Project <span className="required">*</span></label>
                      <select
                        id="project-select"
                        name="project"
                        value={formData.project}
                        onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                        className="form-input"
                        disabled={loading}
                        style={{ padding: '6px 10px', height: '34px', fontSize: '13px' }}
                      >
                        <option value="" disabled hidden>Select project</option>
                        {projects.map((project) => (
                          <option key={project.sfprojectsid} value={project.sfprojectsid}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12px', marginBottom: '4px' }}>Start Date & Time <span className="required">*</span></label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={formData.startTime}
                        onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                        disabled={loading}
                        style={{ padding: '6px 10px', height: '34px', fontSize: '13px' }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12px', marginBottom: '4px' }}>End Date & Time <span className="required">*</span></label>
                      <input
                        type="datetime-local"
                        className="form-input"
                        value={formData.endTime}
                        onChange={e => setFormData({ ...formData, endTime: e.target.value })}
                        disabled={loading}
                        style={{ padding: '6px 10px', height: '34px', fontSize: '13px' }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ fontSize: '12px', marginBottom: '4px' }}>Hours <span className="required">*</span></label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={formData.hours}
                        onChange={e => setFormData({ ...formData, hours: e.target.value })}
                        disabled={loading}
                        style={{ padding: '6px 10px', height: '34px', fontSize: '13px', backgroundColor: '#f9fafb' }}
                      />
                      <span style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px', display: 'block' }}>1 hour breaks automatically deducted.</span>
                    </div>

                    <div className="form-group full-width">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '4px' }}>
                        <label style={{ margin: 0, fontSize: '12px' }}>Description <span className="required">*</span></label>
                        <button
                          type="button"
                          onClick={handleAddToTimesheetList}
                          className="btn-save-item"
                          disabled={loading}
                          style={{ padding: '4px 10px', fontSize: '11px', background: '#e0e7ff', color: '#4f46e5', fontWeight: 'bold', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          {editIndex !== null ? 'Update Entry' : 'Save to List (Draft)'}
                        </button>
                      </div>
                      <ReactQuill
                        theme="snow"
                        modules={quillModules}
                        value={formData.description}
                        onChange={(value) => setFormData({ ...formData, description: value })}
                        placeholder="Describe the work you completed..."
                        className="form-editor-small"
                        style={{ marginBottom: '20px' }}
                      />
                    </div>
                  </div>

                  {/* Pending Submissions below form */}
                  {addedProjects.length > 0 && (
                    <div className="added-projects-section" style={{ marginTop: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0 }}>Pending Submissions ({addedProjects.length})</h3>
                        <div className="total-hours-badge" style={{ background: '#e0e7ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '12px', fontSize: '14px', fontWeight: 'bold' }}>
                          Total Hours: {addedProjects.reduce((sum, entry) => sum + (parseFloat(entry.hours) || 0), 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="added-projects-table">
                          <thead>
                            <tr>
                              <th>Project</th>
                              <th>Time</th>
                              <th>Hours</th>
                              <th>Description</th>
                              <th style={{ width: '120px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {addedProjects.map((entry, index) => {
                              const proj = projects.find(p => p.sfprojectsid === entry.project);
                              return (
                                <tr key={index} onClick={() => handleEditListEntry(index)}>
                                  <td>{proj ? proj.name : entry.project}</td>
                                  <td>{entry.startTime ? formatTime(entry.startTime) : ''} - {entry.endTime ? formatTime(entry.endTime) : ''}</td>
                                  <td><span style={{ fontWeight: 600 }}>{entry.hours}</span></td>
                                  <td>{entry.description.replace(/<[^>]*>/g, '').substring(0, 50)}...</td>
                                  <td>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditListEntry(index);
                                      }}
                                      className="btn-action-edit"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteListEntry(index);
                                      }}
                                      className="btn-action-delete"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </form>

                <div className="form-actions" style={{ padding: '20px 30px', borderTop: '1px solid #e5e7eb', flexShrink: 0, background: 'white', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={handleCancelForm}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleActionClick(e, 'Submitted')}
                    className="btn-primary"
                    disabled={loading || (addedProjects.length === 0 && !formData.project)}
                  >
                    {loading ? 'Submitting...' : 'Submit '}
                  </button>
                </div>
              </>
            ) : (
              /* DETAIL VIEW CONTENT */
              <div className="detail-container" style={{ padding: '20px 30px', margin: 0, flex: '1 1 auto', overflowY: 'auto' }}>
                <div className="detail-content">
                  <div className="detail-row" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Project</label>
                    <div className="detail-value" style={{ fontSize: '15px', color: '#1f2937' }}>
                      {projects.find(p => String(p.id) === String(selectedTimesheet.project) || p.sfprojectsid === selectedTimesheet.project)?.name || 'Unknown'}
                    </div>
                  </div>

                  <div className="detail-row" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Start Date & Time</label>
                    <div className="detail-value" style={{ fontSize: '15px', color: '#1f2937' }}>
                      {selectedTimesheet.starttime
                        ? formatTime(selectedTimesheet.starttime)
                        : (selectedTimesheet.check_in ? formatTime(selectedTimesheet.check_in) : 'Not available')}
                    </div>
                  </div>

                  <div className="detail-row" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>End Date & Time</label>
                    <div className="detail-value" style={{ fontSize: '15px', color: '#1f2937' }}>
                      {selectedTimesheet.endtime
                        ? formatTime(selectedTimesheet.endtime)
                        : (selectedTimesheet.check_out ? formatTime(selectedTimesheet.check_out) : 'Not available')}
                    </div>
                  </div>

                  <div className="detail-row" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Hours</label>
                    <div className="detail-value" style={{ fontSize: '15px', color: '#1f2937' }}>
                      {selectedTimesheet.hours
                        ? <span style={{ fontWeight: 600 }}>{Number(selectedTimesheet.hours).toFixed(2)} hrs</span>
                        : 'N/A'}
                    </div>
                  </div>

                  <div className="detail-row" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Description</label>
                    <div
                      className="detail-value"
                      style={{ fontSize: '15px', color: '#1f2937' }}
                      dangerouslySetInnerHTML={{ __html: selectedTimesheet.comments }}
                    />
                  </div>

                  <div className="detail-row" style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Status</label>
                    <div className="detail-value" style={{ fontSize: '15px', color: '#1f2937' }}>
                      {selectedTimesheet.hasbeensubmitted === 'No' ? (
                        <span style={{ backgroundColor: '#e0e7ff', color: '#4f46e5', padding: '6px 16px', borderRadius: '16px', fontSize: '14px', fontWeight: 'bold' }}>Saved</span>
                      ) : (
                        <span style={{ backgroundColor: '#d1fae5', color: '#065f46', padding: '6px 16px', borderRadius: '16px', fontSize: '14px', fontWeight: 'bold' }}>✓ Submitted</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {showCancelModal && (
        <div className="timesheet-drawer-overlay" style={{ zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Unsaved Changes</h3>
            <p style={{ margin: '0 0 24px 0', color: '#4b5563' }}>You have unsaved changes in your timesheet. What would you like to do?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                className="btn-secondary"
                style={{ background: '#f3f4f6', color: '#374151', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}
              >
                Return to Form
              </button>
              <button
                type="button"
                onClick={handleDiscardDrafts}
                style={{ background: '#ef4444', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', whiteSpace: 'nowrap' }}
              >
                Discard Changes
              </button>
              <button
                type="button"
                onClick={(e) => { setShowCancelModal(false); handleActionClick(e, 'Saved'); }}
                className="btn-primary"
                style={{ background: '#4f46e5', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', whiteSpace: 'nowrap' }}
              >
                Save as Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return isEmbedded ? pageContent : <MainLayout title="Timesheet">{pageContent}</MainLayout>;
}



